import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { EVENTS, SCHEDULE_EVENT_TYPES, SCHEDULE_ANALYTICS_VIEW_TYPE } from 'shared/constants';
import analyticsService from 'src/helpers/analytics.service';
import { useEducationalEventFormat } from './use-educational-event-format';

export const REPORT_VIEW = { PERSON: 'person', EVENT: 'event' };

export const reportViewType = view =>
  view === REPORT_VIEW.EVENT
    ? SCHEDULE_ANALYTICS_VIEW_TYPE.BY_EVENT
    : SCHEDULE_ANALYTICS_VIEW_TYPE.BY_PERSON;

const PARAMS = {
  VIEW: 'view',
  PERSON: 'person',
  EVENT_DETAIL: 'event-detail',
  FROM: 'from',
  UNTIL: 'until',
};

const DEFAULT_PAST_DAYS = 180;
const DATE_FMT = 'yyyy-MM-dd';

/**
 * Default filter window: [today − 180d, today], as yyyy-MM-dd strings.
 * Mirrors the events list's 180-day past window.
 */
function defaultRange() {
  const until = DateTime.now();
  return {
    from: until.minus({ days: DEFAULT_PAST_DAYS }).toFormat(DATE_FMT),
    until: until.toFormat(DATE_FMT),
  };
}

/**
 * URL-driven state for the Reports tab. Exposes the current view (person/event),
 * the drill-down selection, the event multi-select, and the date range — plus
 * setters that write back to the query string (replace, no history spam), and
 * `apiRange` which converts the day-granular pickers into ISO instants at the
 * org-timezone day boundaries for the backend `from`/`until` params.
 */
export function useReportFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orgTimezone } = useEducationalEventFormat();

  const fallback = useMemo(defaultRange, []);

  const view =
    searchParams.get(PARAMS.VIEW) === REPORT_VIEW.EVENT ? REPORT_VIEW.EVENT : REPORT_VIEW.PERSON;
  const personId = searchParams.get(PARAMS.PERSON) || null;
  const eventDetailId = searchParams.get(PARAMS.EVENT_DETAIL) || null;
  const from = searchParams.get(PARAMS.FROM) || fallback.from;
  const until = searchParams.get(PARAMS.UNTIL) || fallback.until;

  const patchParams = useCallback(
    (updater, { push = false } = {}) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          updater(next);
          return next;
        },
        { replace: !push },
      );
    },
    [setSearchParams],
  );

  const setView = useCallback(
    nextView =>
      patchParams(next => {
        next.set(PARAMS.VIEW, nextView);
        next.delete(PARAMS.PERSON);
        next.delete(PARAMS.EVENT_DETAIL);
      }),
    [patchParams],
  );

  // Opening/closing a drill-down is real navigation — push a history entry so
  // Back returns to the list rather than replacing (and skipping) it.
  const openPerson = useCallback(
    userId => {
      analyticsService.track(EVENTS.SCHEDULE.PROVIDER_REPORT_OPEN, {
        type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
        providerId: userId,
      });
      patchParams(
        next => {
          next.delete(PARAMS.EVENT_DETAIL);
          next.set(PARAMS.PERSON, userId);
        },
        { push: true },
      );
    },
    [patchParams],
  );
  const openEvent = useCallback(
    id => {
      analyticsService.track(EVENTS.SCHEDULE.EVENT_REPORT_OPEN, {
        type: SCHEDULE_EVENT_TYPES.EDUCATIONAL,
        eventId: id,
      });
      patchParams(
        next => {
          next.delete(PARAMS.PERSON);
          next.set(PARAMS.EVENT_DETAIL, id);
        },
        { push: true },
      );
    },
    [patchParams],
  );
  const closeDrillDown = useCallback(
    () =>
      patchParams(
        next => {
          next.delete(PARAMS.PERSON);
          next.delete(PARAMS.EVENT_DETAIL);
        },
        { push: true },
      ),
    [patchParams],
  );

  const setRange = useCallback(
    ({ from: nextFrom, until: nextUntil }) =>
      patchParams(next => {
        if (nextFrom) next.set(PARAMS.FROM, nextFrom);
        if (nextUntil) next.set(PARAMS.UNTIL, nextUntil);
      }),
    [patchParams],
  );

  // Both bounds are day-start instants so the range reads as `[from, until)`:
  // `until` is the start of the day *after* the selected one, which keeps the
  // selected day fully included while matching the backend's exclusive `$lt`.
  const apiRange = useMemo(() => {
    const zone = orgTimezone || 'utc';
    const fromISO = DateTime.fromFormat(from, DATE_FMT, { zone }).startOf('day');
    const untilISO = DateTime.fromFormat(until, DATE_FMT, { zone })
      .plus({ days: 1 })
      .startOf('day');
    return {
      from: fromISO.isValid ? fromISO.toUTC().toISO() : undefined,
      until: untilISO.isValid ? untilISO.toUTC().toISO() : undefined,
    };
  }, [from, until, orgTimezone]);

  return {
    view,
    personId,
    eventDetailId,
    from,
    until,
    apiRange,
    setView,
    openPerson,
    openEvent,
    closeDrillDown,
    setRange,
  };
}
